import React, { useState, useEffect } from 'react';
import { http } from '../../lib/http';

import CustomerEditModal from './CustomerEditModal';
import { fetchLivePricing, lookupPlanPrice, sumAddOnsPrice } from '../../lib/pricing';
import { Button } from '../ui/button';
import { nextServiceDayToronto } from '../../lib/time';

const CustomersTab = ({ setMessage, setError }) => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);

    // Status filter state
    const [statusFilter, setStatusFilter] = useState('active');

    // Date filter state
    const [planStartDateFilter, setPlanStartDateFilter] = useState('');
    const [planRenewalDateFilter, setPlanRenewalDateFilter] = useState('');

    // Payment status filter (Active tab only)
    const [unpaidFilter, setUnpaidFilter] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const PAGE_SIZE = 50;  // Load 50 customers per page

    // Pause modal state
    const [showPauseModal, setShowPauseModal] = useState(false);
    const [pausingCustomer, setPausingCustomer] = useState(null);
    const [pauseType, setPauseType] = useState('custom_date'); // 'custom_date' or 'custom_range'
    const [pauseStartDate, setPauseStartDate] = useState('');
    const [pauseEndDate, setPauseEndDate] = useState('');
    const [pauseReason, setPauseReason] = useState('');

    // CSV upload state
    const [isUploading, setIsUploading] = useState(false);

    // Import customers state
    const [importFile, setImportFile] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [importPreview, setImportPreview] = useState(null);

    // Create customer state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [creatingCustomer, setCreatingCustomer] = useState(false);
    const [livePricing, setLivePricing] = useState(null);
    const NEW_CUSTOMER_DEFAULTS = {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        street: '',
        city: '',
        postal_code: '',
        property_type: 'House',
        delivery_instruction: '',
        meal_plan_type: '2 Meals a Day',
        meal_plan_term: '5 Days Veg',
        nv_preference: '',
        nv_days: [],
        add_ons: [],
        plan_term: 'Monthly',
        plan_start_date: nextServiceDayToronto(),
        plan_end_date: '',
        plan_renewal_date: '',
        additional_charges: 0,
        additional_discount: 0,
        amount_paid: 0
    };
    const [newCustomer, setNewCustomer] = useState(NEW_CUSTOMER_DEFAULTS);

    // Fetch live pricing when create modal opens
    React.useEffect(() => {
        if (showCreateModal && !livePricing) {
            fetchLivePricingData();
        }
    }, [showCreateModal, livePricing]);

    // Auto-calculate renewal and end dates for new customer
    React.useEffect(() => {
        if (newCustomer.plan_start_date && newCustomer.plan_term) {
            const term = newCustomer.plan_term.toLowerCase();
            // Use noon UTC to avoid DST/timezone boundary issues
            const start = new Date(newCustomer.plan_start_date + 'T12:00:00Z');
            if (isNaN(start.getTime())) return;

            let endDate;

            if (term === 'weekly') {
                // Count 5 Mon-Fri service days from start (inclusive) — matches backend addServiceDaysToDate
                let current = new Date(start);
                let counted = 0;
                while (counted < 5) {
                    const dow = current.getUTCDay(); // 0=Sun, 6=Sat
                    if (dow >= 1 && dow <= 5) {
                        counted++;
                        if (counted === 5) break;
                    }
                    current = new Date(current.getTime() + 86400000);
                }
                endDate = current;
            } else if (term === 'monthly') {
                // start + 1 month − 1 day — matches backend calculateEndDate monthly logic
                const y = start.getUTCFullYear(), m = start.getUTCMonth(), d = start.getUTCDate();
                const oneMonthLater = new Date(Date.UTC(y, m + 1, d));
                endDate = new Date(oneMonthLater.getTime() - 86400000);
            } else {
                return;
            }

            // renewalDeadline = endDate + 1 calendar day — matches backend calculateRenewalDeadline date
            const renewal = new Date(endDate.getTime() + 86400000);

            setNewCustomer(prev => ({
                ...prev,
                plan_renewal_date: renewal.toISOString().split('T')[0],
                plan_end_date: endDate.toISOString().split('T')[0]
            }));
        }
    }, [newCustomer.plan_start_date, newCustomer.plan_term]);

    const fetchLivePricingData = async () => {
        try {
            const pricing = await fetchLivePricing();
            if (pricing) {
                setLivePricing(pricing);
            }
        } catch (error) {
            console.error('Failed to fetch pricing:', error);
            setError('Failed to load pricing data');
        }
    };

    const loadCustomers = async (page = 1, append = false) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
            setCustomers([]);
            setCurrentPage(1);
        }

        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams({
                page: page.toString(),
                limit: PAGE_SIZE.toString(),
                status_filter: statusFilter  // Use state instead of hardcoded 'active'
            });

            if (search.trim()) {
                params.append('search', search.trim());
            }

            // NEW: Add date filters if set
            if (planStartDateFilter) {
                params.append('plan_start_date_filter', planStartDateFilter);
            }

            if (planRenewalDateFilter) {
                params.append('plan_renewal_date_filter', planRenewalDateFilter);
            }

            if (unpaidFilter) {
                params.append('payment_status_filter', 'unpaid');
            }

            const response = await http.get(
                `/admin/customers/enhanced?${params}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            const newCustomers = response.data.data.customers || [];
            console.log(newCustomers);
            if (append) {
                setCustomers(prev => [...prev, ...newCustomers]);
            } else {
                setCustomers(newCustomers);
            }

            // Update pagination state
            const pagination = response.data.data.pagination;
            if (pagination) {
                setTotalCount(pagination.total_count);
                setHasMore(pagination.has_next);
                setCurrentPage(page);
            }

        } catch (error) {
            console.error('Failed to load customers:', error);
            setError(error.response?.data?.detail || error.response?.data?.message || 'Failed to load customers');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const loadMoreCustomers = () => {
        if (!loadingMore && hasMore) {
            loadCustomers(currentPage + 1, true);
        }
    };

    // Debounced search - reload from page 1 when search changes
    // Skip initial mount (handled by filter effect below)
    const isFirstSearchRender = React.useRef(true);
    React.useEffect(() => {
        if (isFirstSearchRender.current) {
            isFirstSearchRender.current = false;
            return;
        }
        const timeoutId = setTimeout(() => {
            loadCustomers(1, false);
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [search]);

    // Reset unpaid filter when leaving Active tab
    React.useEffect(() => {
        if (statusFilter !== 'active') {
            setUnpaidFilter(false);
        }
    }, [statusFilter]);

    // Load customers on mount AND when filters change
    React.useEffect(() => {
        loadCustomers(1, false);
    }, [planStartDateFilter, planRenewalDateFilter, statusFilter, unpaidFilter]);

    const markPaid = async (customerId, customerName) => {
        try {
            const token = localStorage.getItem('token');
            const response = await http.post(
                `/admin/customers/${customerId}/mark-paid`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            setMessage(`${customerName}: ${response.data.message}`);
            loadCustomers();

        } catch (error) {
            console.error('Failed to mark paid:', error);
            setError(error.response?.data?.detail || 'Failed to mark payment');
        }
    };

    const cancelSubscription = async (customerId, customerName) => {
        const reason = prompt('Cancellation reason:', 'Customer request');
        if (!reason) return;

        try {
            const token = localStorage.getItem('token');
            const url = `/admin/customers/${customerId}/cancel`;

            const response = await http.post(url, { reason }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setMessage(`✅ Cancelled ${customerName}: ${response.data.message}`);
                // Reload customers to remove the canceled customer from active list
                loadCustomers();
            } else {
                setError(`❌ Cancel failed: ${response.data.message || 'Unknown error'}`);
            }

        } catch (error) {
            console.error('❌ Failed to cancel subscription:', error);
            const errorMsg = error.response?.data?.detail || error.message || 'Failed to cancel subscription';
            setError(`❌ Cancel failed: ${errorMsg}`);
        }
    };

    const activateCustomer = async (customerId, customerName) => {
        if (!window.confirm(`Activate subscription for ${customerName} (Unpaid)?\n\nThis will mark them as ACTIVE but PENDING payment.`)) return;

        try {
            const token = localStorage.getItem('token');
            const response = await http.post(`/admin/customers/${customerId}/activate`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setMessage(`✅ ${response.data.message}`);
                loadCustomers();
            } else {
                setError(`❌ Activation failed: ${response.data.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('❌ Failed to activate customer:', error);
            setError(error.response?.data?.message || 'Failed to activate customer');
        }
    };

    const openPauseModal = (customer) => {
        setPausingCustomer(customer);
        setPauseType('custom_date');
        // Set default to tomorrow
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setPauseStartDate(tomorrow.toISOString().split('T')[0]);
        setPauseEndDate('');
        setPauseReason('');
        setShowPauseModal(true);
    };

    const pauseCustomer = async () => {
        if (!pausingCustomer) return;

        // Validation - skip date validation for indefinite pause
        if (pauseType === 'custom_date') {
            if (!pauseStartDate) {
                setError('Please select a date');
                return;
            }
        }

        if (pauseType === 'custom_range') {
            if (!pauseStartDate || !pauseEndDate) {
                setError('Please select both start and end dates');
                return;
            }
            if (new Date(pauseEndDate) < new Date(pauseStartDate)) {
                setError('End date must be after start date');
                return;
            }
        }

        try {
            const token = localStorage.getItem('token');
            const customerId = pausingCustomer.customer_id || pausingCustomer.id;
            const customerName = `${pausingCustomer.first_name} ${pausingCustomer.last_name}`;

            // Map frontend pause types to backend expected values
            let pauseData;
            if (pauseType === 'custom_date') {
                pauseData = { type: 'single_day', pauseDate: pauseStartDate };
            } else if (pauseType === 'custom_range') {
                pauseData = { type: 'date_range', startDate: pauseStartDate, endDate: pauseEndDate };
            } else {
                // indefinite
                pauseData = { type: 'indefinite', startDate: new Date().toISOString().split('T')[0] };
            }

            const response = await http.post(
                `/admin/customers/${customerId}/pause`,
                pauseData,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                let message;
                if (pauseType === 'indefinite') {
                    message = `✅ Paused ${customerName} indefinitely. Unpause to resume service.`;
                } else {
                    message = `✅ Paused ${customerName} starting ${response.data.pause_start_date || pauseStartDate}. Renewal date will be adjusted upon unpause.`;
                }

                setMessage(message);
                setShowPauseModal(false);
                setPausingCustomer(null);
                loadCustomers();
            } else {
                setError(`❌ Pause failed: ${response.data.message || 'Unknown error'}`);
            }

        } catch (error) {
            console.error('❌ Failed to pause subscription:', error);
            const errorMsg = error.response?.data?.detail || error.message || 'Failed to pause subscription';
            setError(`❌ Pause failed: ${errorMsg}`);
        }
    };

    const unpauseCustomer = async (customerId, customerName) => {
        const confirmUnpause = window.confirm(`Unpause subscription for ${customerName}? Renewal date will be adjusted based on pause duration.`);
        if (!confirmUnpause) return;

        try {
            const token = localStorage.getItem('token');
            const response = await http.post(
                `/admin/customers/${customerId}/unpause`,
                { reason: 'Admin unpaused' },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                const adjustedDays = response.data.adjusted_days || 0;
                const newRenewalDate = response.data.new_renewal_date;

                let message = `✅ Unpaused ${customerName}`;
                if (adjustedDays > 0 && newRenewalDate) {
                    message += `. Renewal date adjusted by ${adjustedDays} day${adjustedDays !== 1 ? 's' : ''} to ${newRenewalDate}`;
                }

                setMessage(message);
                loadCustomers();
            } else {
                setError(`❌ Unpause failed: ${response.data.message || 'Unknown error'}`);
            }

        } catch (error) {
            console.error('❌ Failed to unpause subscription:', error);
            const errorMsg = error.response?.data?.detail || error.message || 'Failed to unpause subscription';
            setError(`❌ Unpause failed: ${errorMsg}`);
        }
    };

    const exportToDispatch = async () => {
        if (selectedCustomers.length === 0) {
            setError('Please select customers to export');
            return;
        }

        setMessage(`Export to Dispatch: ${selectedCustomers.length} customers selected (feature coming in Phase 4)`);
    };

    const handleEdit = (customer) => {
        setEditingCustomer(customer);
        setShowEditModal(true);
    };

    const createLogin = async (customerId, customerName) => {
        if (!window.confirm(`Create login account for ${customerName}?\n\nDefault password: login`)) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await http.post(
                `/admin/customers/${customerId}/create-login`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setMessage(`✅ ${response.data.message}`);
            } else {
                setError(response.data.message || 'User account already exists');
            }
        } catch (error) {
            console.error('Failed to create login:', error);
            setError(error.response?.data?.detail || 'Failed to create login account');
        }
    };

    const saveEdit = async (updates, postAction = null) => {
        try {
            const customerId = editingCustomer.customer_id;
            const token = localStorage.getItem('token');

            // Separate preference updates
            const preferenceFields = ['food_preferences', 'ingredients_to_avoid', 'allergy_info', 'general_feedback'];
            const preferenceUpdates = {};
            const standardUpdates = { ...updates };

            let hasPreferenceUpdates = false;
            preferenceFields.forEach(field => {
                if (field in updates) {
                    preferenceUpdates[field] = updates[field];
                    delete standardUpdates[field];
                    hasPreferenceUpdates = true;
                }
            });

            const promises = [];

            // 1. Save standard updates if any
            if (Object.keys(standardUpdates).length > 0) {
                promises.push(
                    http.put(
                        `/admin/customers/${customerId}`,
                        standardUpdates,
                        { headers: { Authorization: `Bearer ${token}` } }
                    )
                );
            }

            // 2. Save preference updates if any
            if (hasPreferenceUpdates) {
                promises.push(
                    http.post(
                        `/admin/customer-preferences/${customerId}`,
                        preferenceUpdates,
                        { headers: { Authorization: `Bearer ${token}` } }
                    )
                );
            }

            if (promises.length === 0) {
                setShowEditModal(false);
                return;
            }

            await Promise.all(promises);

            // 3. Handle post-save actions (Renew or Activate)
            if (postAction === 'renew') {
                const response = await http.post(`/admin/customers/${customerId}/renew`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data.success) {
                    setMessage('✅ Customer updated and Renewed (Active & Paid)');
                }
            } else if (postAction === 'activate') {
                const response = await http.post(`/admin/customers/${customerId}/activate`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data.success) {
                    setMessage('✅ Customer updated and Activated (Active & Unpaid)');
                }
            } else {
                setMessage('✅ Customer details updated successfully');
            }

            setShowEditModal(false);

            // Reload customers to show changes
            await loadCustomers();

        } catch (error) {
            console.error('Failed to update customer:', error.message);
            setError(error.response?.data?.detail || error.response?.data?.message || 'Failed to update customer');
        }
    };

    // EMERGENCY BACKUP - Download ALL data
    const downloadEmergencyBackup = async () => {
        try {
            setMessage('🔥 Creating emergency backup of ALL data...');
            const token = localStorage.getItem('token');

            const response = await http.get('/admin/emergency-backup-all-data', {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const timestamp = new Date().toISOString().replace(/:/g, '-').split('.')[0];
            link.setAttribute('download', `login_backup_${timestamp}.json`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            setMessage('✅ EMERGENCY BACKUP downloaded successfully! Keep this file safe.');
        } catch (error) {
            console.error('Failed to create backup:', error);
            setError('Failed to create emergency backup');
        }
    };

    // CSV Bulk Upload Handler
    const handleCsvUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.name.toLowerCase().endsWith('.csv')) {
            setError('Please upload a CSV file only');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setError('File is too large. Maximum size is 5MB.');
            return;
        }

        setMessage('📊 Uploading CSV file...');
        setIsUploading(true);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const token = localStorage.getItem('token');
            // Use the native fetch API or http wrapper
            const response = await http.post('/admin/customers/upload-csv', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.data.success) {
                setMessage(`✅ ${response.data.message}`);
                setTimeout(() => {
                    loadCustomers();
                    setMessage('');
                }, 5000);
            } else {
                setError(`Upload failed: ${response.data.message}`);
            }
        } catch (error) {
            console.error('CSV Upload Error:', error);
            setError(error.response?.data?.message || 'Failed to upload CSV file');
        } finally {
            setIsUploading(false);
            // Reset the input
            event.target.value = '';
        }
    };

    // FUNCTIONS_PLACEHOLDER

    // Helper to format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-CA', {
            style: 'currency',
            currency: 'CAD'
        }).format(amount || 0);
    };

    // Helper: get available meal plan terms for the selected type from pricing
    const getMealPlanTerms = () => {
        if (!livePricing?.plans) return [];
        const keys = Object.keys(livePricing.plans[newCustomer.meal_plan_type] || {});
        // Extract unique base terms (part before |)
        const baseTerms = [...new Set(keys.map(k => k.split('|')[0]))];
        return baseTerms;
    };

    // Helper: get NV preference sub-options for the selected type+term
    const getNvPreferences = () => {
        if (!livePricing?.plans) return [];
        const allKeys = Object.keys(livePricing.plans[newCustomer.meal_plan_type] || {});
        // Find keys that start with the selected base term and have a | separator
        const matching = allKeys.filter(k => k.startsWith(newCustomer.meal_plan_term + '|'));
        return matching.map(k => k.split('|')[1]);
    };

    // Helper: calculate plan price from pricing data
    const getCalculatedPlanPrice = () => {
        return lookupPlanPrice(
            newCustomer.meal_plan_type,
            newCustomer.meal_plan_term,
            newCustomer.nv_preference,
            newCustomer.plan_term
        );
    };

    // Helper: calculate total price including add-ons
    const getCalculatedTotalPrice = () => {
        const planPrice = getCalculatedPlanPrice();
        const addOnsPrice = sumAddOnsPrice(newCustomer.add_ons, newCustomer.plan_term);
        return planPrice + addOnsPrice;
    };

    const handleCreateCustomer = async () => {
        // Basic Validation
        if (!newCustomer.first_name || !newCustomer.last_name || !newCustomer.email) {
            setError('Please fill in required fields (Name, Email)');
            return;
        }

        if (newCustomer.plan_start_date && newCustomer.plan_end_date) {
            if (new Date(newCustomer.plan_end_date) < new Date(newCustomer.plan_start_date)) {
                setError('End date must be after start date');
                return;
            }
        }

        setCreatingCustomer(true);

        try {
            const token = localStorage.getItem('token');

            // Build the plan term label (e.g. "5 Days Non-Veg|1 NV/day" or "5 Days Veg")
            const mealPlanTermLabel = newCustomer.nv_preference
                ? `${newCustomer.meal_plan_term}|${newCustomer.nv_preference}`
                : newCustomer.meal_plan_term;

            const planOnlyPrice = getCalculatedPlanPrice();
            const amountPaid = newCustomer.amount_paid || planOnlyPrice;

            const customerData = {
                first_name: newCustomer.first_name,
                last_name: newCustomer.last_name,
                email: newCustomer.email,
                phone: newCustomer.phone,
                address: {
                    street: newCustomer.street,
                    city: newCustomer.city,
                    postal_code: newCustomer.postal_code,
                    delivery_instructions: newCustomer.delivery_instruction,
                    property_type: newCustomer.property_type
                },

                plan: {
                    meal_plan_type: newCustomer.meal_plan_type,
                    meal_plan_term: mealPlanTermLabel,
                    billing_term: newCustomer.plan_term,
                    plan_start_date: newCustomer.plan_start_date,
                    plan_end_date: newCustomer.plan_end_date,
                    plan_renewal_date: newCustomer.plan_renewal_date,
                    add_ons: newCustomer.add_ons.map(addonName => ({
                        name: addonName,
                        price: livePricing?.addons?.[addonName]?.[newCustomer.plan_term] || 0
                    })),
                    nv_days: newCustomer.nv_days || []
                },

                payment: {
                    status: amountPaid > 0 ? 'paid' : 'pending',
                    amount: amountPaid
                }
            };

            const response = await http.post('/admin/customers', customerData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setMessage('✅ Customer created successfully!');
                setShowCreateModal(false);
                setNewCustomer({ ...NEW_CUSTOMER_DEFAULTS, plan_start_date: nextServiceDayToronto() });
                loadCustomers();
            } else {
                setError(response.data.message || 'Failed to create customer');
            }

        } catch (error) {
            console.error('Create error:', error);
            setError(error.response?.data?.message || 'Failed to create customer');
        } finally {
            setCreatingCustomer(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header & Actions */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 w-full md:w-auto">
                    <div className="relative flex-1 md:w-64">
                        <input
                            type="text"
                            placeholder="Search customers..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <svg
                            className="w-5 h-5 text-gray-400 absolute left-3 top-2.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    <Button
                        onClick={() => loadCustomers()}
                        variant="outline"
                        className="p-2"
                        title="Refresh"
                    >
                        🔄
                    </Button>
                </div>

                <div className="flex flex-wrap gap-2 w-full md:w-auto justify-end">
                    <Button
                        onClick={() => setShowCreateModal(true)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                    >
                        + New Customer
                    </Button>
                    <div className="relative">
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleCsvUpload}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <Button
                            variant="outline"
                            className="border-blue-200 text-blue-700 hover:bg-blue-50"
                        >
                            📑 Upload CSV
                        </Button>
                    </div>
                    <Button
                        onClick={downloadEmergencyBackup}
                        variant="outline"
                        className="border-red-200 text-red-700 hover:bg-red-50"
                        title="Download full backup"
                    >
                        🔥 Backup
                    </Button>
                </div>
            </div>

            {/* Status Tabs */}
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                {['active', 'upcoming', 'paused', 'expired', 'ExClient'].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setStatusFilter(tab)}
                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${statusFilter === tab
                            ? 'bg-white text-orange-600 shadow-sm ring-1 ring-gray-200'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                            }`}
                    >
                        {tab === 'ExClient' ? 'Ex-Customers' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                        {statusFilter === tab && totalCount > 0 && (
                            <span className="ml-2 bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full text-[10px]">
                                {totalCount}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Date Filters */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex flex-wrap gap-4 items-center">
                <span className="text-sm font-semibold text-blue-800">📅 Filter by Date:</span>
                <div className="flex items-center gap-2">
                    <label className="text-xs text-blue-600 uppercase font-bold">Plan Start:</label>
                    <input
                        type="date"
                        value={planStartDateFilter}
                        onChange={(e) => setPlanStartDateFilter(e.target.value)}
                        className="border border-blue-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-xs text-blue-600 uppercase font-bold">Renewal Date:</label>
                    <input
                        type="date"
                        value={planRenewalDateFilter}
                        onChange={(e) => setPlanRenewalDateFilter(e.target.value)}
                        className="border border-blue-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                {statusFilter === 'active' && (
                    <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={unpaidFilter}
                                onChange={(e) => setUnpaidFilter(e.target.checked)}
                                className="w-4 h-4 rounded border-blue-300 text-orange-600 focus:ring-orange-500"
                            />
                            <span className="text-xs text-blue-600 uppercase font-bold">Unpaid</span>
                        </label>
                    </div>
                )}
                {(planStartDateFilter || planRenewalDateFilter || unpaidFilter) && (
                    <button
                        onClick={() => {
                            setPlanStartDateFilter('');
                            setPlanRenewalDateFilter('');
                            setUnpaidFilter(false);
                        }}
                        className="text-xs text-red-600 hover:text-red-800 underline ml-2"
                    >
                        Clear Filters
                    </button>
                )}
            </div>

            {/* Customer List */}
            <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Customer
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Contact
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Plan Details
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                        <div className="flex justify-center items-center space-x-2">
                                            <div className="w-4 h-4 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                                            <div className="w-4 h-4 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                            <div className="w-4 h-4 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                        </div>
                                        <p className="mt-2 text-sm">Loading customers...</p>
                                    </td>
                                </tr>
                            ) : customers.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                        No customers found matching your criteria.
                                    </td>
                                </tr>
                            ) : (
                                customers.map((customer) => (
                                    <tr key={customer.customer_id} className={`hover:bg-gray-50 transition-colors ${customer.is_paused ? 'bg-yellow-50' : ''}`}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10">
                                                    <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold ${customer.is_paused ? 'bg-yellow-500' : 'bg-orange-500'
                                                        }`}>
                                                        {customer.first_name?.[0]}{customer.last_name?.[0]}
                                                    </div>
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {customer.first_name} {customer.last_name}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        ID: {customer.customer_id_display || customer.customer_id?.substring(0, 8) || 'N/A'}
                                                    </div>
                                                    {customer.is_paused && (
                                                        <div className="mt-1 space-y-1">
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                                                ⏸️ PAUSED
                                                            </span>
                                                            <div className="text-[10px] text-yellow-700 font-semibold bg-yellow-50 p-1 rounded border border-yellow-200">
                                                                From: {customer.pause_start_date || customer.plan_start_date || 'Since Import'}<br />
                                                                To: {customer.pause_end_date || 'Indefinite'}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{customer.email}</div>
                                            <div className="text-sm text-gray-500">{customer.phone}</div>
                                            <div className="text-xs text-gray-400 mt-1 max-w-[200px] truncate" title={customer.street + ', ' + customer.city}>
                                                {customer.street}, {customer.city}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 font-medium">
                                                {customer.meal_plan_term || 'No Plan'}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {customer.plan_term} Billing
                                            </div>
                                            <div className="text-xs mt-1">
                                                <span className="text-gray-500">Starts: </span>
                                                <span className="font-medium">{customer.plan_start_date || 'N/A'}</span>
                                            </div>
                                            <div className="text-xs">
                                                <span className="text-gray-500">Ends: </span>
                                                <span className="font-medium">{customer.plan_end_date || 'N/A'}</span>
                                            </div>
                                            <div className="text-xs">
                                                <span className="text-gray-500">Renews: </span>
                                                <span className={`font-medium ${new Date(customer.plan_renewal_date) < new Date() ? 'text-red-600 font-bold' : 'text-green-600'
                                                    }`}>
                                                    {customer.plan_renewal_date || 'N/A'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div>
                                                {customer.is_paid ? (
                                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                        Paid
                                                    </span>
                                                ) : (
                                                    <div className="flex flex-col gap-1">
                                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                                            Unpaid
                                                        </span>
                                                        <button
                                                            onClick={() => markPaid(customer.customer_id, `${customer.first_name} ${customer.last_name}`)}
                                                            className="bg-green-100 text-green-800 hover:bg-green-200 px-2 py-0.5 rounded-full text-[10px] font-bold cursor-pointer border border-green-200"
                                                        >
                                                            Mark Paid
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="mt-2 text-xs">
                                                {customer.has_login ? (
                                                    <span className="text-blue-600 flex items-center gap-1">
                                                        ✅ Has Login
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={() => createLogin(customer.customer_id, `${customer.first_name} ${customer.last_name}`)}
                                                        className="text-orange-600 hover:text-orange-800 underline"
                                                    >
                                                        Create Login
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex flex-col gap-2 items-end">
                                                {/* Active Tab Actions */}
                                                {statusFilter === 'active' && (
                                                    <>
                                                        <button
                                                            onClick={() => handleEdit(customer)}
                                                            className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 px-3 py-1 rounded hover:bg-indigo-100 w-24 text-center transition-colors"
                                                        >
                                                            Edit
                                                        </button>
                                                        {customer.is_paused ? (
                                                            <button
                                                                onClick={() => unpauseCustomer(customer.customer_id, `${customer.first_name} ${customer.last_name}`)}
                                                                className="text-green-600 hover:text-green-900 bg-green-50 px-3 py-1 rounded hover:bg-green-100 w-24 text-center transition-colors"
                                                            >
                                                                ▶ Resume
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={() => openPauseModal(customer)}
                                                                className="text-yellow-600 hover:text-yellow-900 bg-yellow-50 px-3 py-1 rounded hover:bg-yellow-100 w-24 text-center transition-colors"
                                                            >
                                                                ⏸ Pause
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => cancelSubscription(customer.customer_id, `${customer.first_name} ${customer.last_name}`)}
                                                            className="text-red-600 hover:text-red-900 bg-red-50 px-3 py-1 rounded hover:bg-red-100 w-24 text-center transition-colors"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </>
                                                )}

                                                {/* Ex-Client Tab Actions */}
                                                {(statusFilter === 'ExClient' || statusFilter === 'all') && (customer.status === 'ExClient' || customer.status === 'ex-customer' || customer.status === 'cancelled' || customer.status === 'inactive') && (
                                                    <button
                                                        onClick={() => handleEdit(customer)}
                                                        className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 px-3 py-1 rounded hover:bg-indigo-100 w-24 text-center transition-colors"
                                                    >
                                                        Edit
                                                    </button>
                                                )}

                                                {/* Paused Tab Actions */}
                                                {statusFilter === 'paused' && (
                                                    <button
                                                        onClick={() => unpauseCustomer(customer.customer_id, `${customer.first_name} ${customer.last_name}`)}
                                                        className="text-green-600 hover:text-green-900 bg-green-50 px-3 py-1 rounded hover:bg-green-100 w-24 text-center transition-colors"
                                                    >
                                                        ▶ Resume
                                                    </button>
                                                )}

                                                {/* Expired Tab Actions */}
                                                {statusFilter === 'expired' && (
                                                    <button
                                                        onClick={() => handleEdit(customer)}
                                                        className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 px-3 py-1 rounded hover:bg-indigo-100 w-24 text-center transition-colors"
                                                    >
                                                        Edit
                                                    </button>
                                                )}

                                                {/* Default fallback for other search cases if any */}
                                                {statusFilter !== 'active' && statusFilter !== 'paused' && statusFilter !== 'expired' && statusFilter !== 'ExClient' && (
                                                    <button
                                                        onClick={() => handleEdit(customer)}
                                                        className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 px-3 py-1 rounded hover:bg-indigo-100 w-24 text-center transition-colors"
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {/* Load More Button */}
                {hasMore && customers.length > 0 && (
                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-center">
                        <Button
                            onClick={loadMoreCustomers}
                            disabled={loadingMore}
                            variant="secondary"
                            className="w-full md:w-auto"
                        >
                            {loadingMore ? 'Loading more...' : 'Load More Customers'}
                        </Button>
                    </div>
                )}
            </div>

            {/* Create Customer Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full m-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6 border-b pb-4">
                            <h3 className="text-xl font-bold">Add New Customer</h3>
                            <button onClick={() => setShowCreateModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Personal Info */}
                            <div className="space-y-4">
                                <h4 className="font-semibold text-gray-700 border-b pb-2">Personal Information</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">First Name *</label>
                                        <input
                                            type="text"
                                            value={newCustomer.first_name}
                                            onChange={(e) => setNewCustomer({ ...newCustomer, first_name: e.target.value })}
                                            className="w-full border rounded px-3 py-2"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Last Name *</label>
                                        <input
                                            type="text"
                                            value={newCustomer.last_name}
                                            onChange={(e) => setNewCustomer({ ...newCustomer, last_name: e.target.value })}
                                            className="w-full border rounded px-3 py-2"
                                            required
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Email *</label>
                                    <input
                                        type="email"
                                        value={newCustomer.email}
                                        onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                                        className="w-full border rounded px-3 py-2"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Phone</label>
                                    <input
                                        type="tel"
                                        value={newCustomer.phone}
                                        onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                                        className="w-full border rounded px-3 py-2"
                                    />
                                </div>
                            </div>

                            {/* Address Info */}
                            <div className="space-y-4">
                                <h4 className="font-semibold text-gray-700 border-b pb-2">Delivery Address</h4>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Street Address</label>
                                    <input
                                        type="text"
                                        value={newCustomer.street}
                                        onChange={(e) => setNewCustomer({ ...newCustomer, street: e.target.value })}
                                        className="w-full border rounded px-3 py-2"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">City</label>
                                        <input
                                            type="text"
                                            value={newCustomer.city}
                                            onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                                            className="w-full border rounded px-3 py-2"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Postal Code</label>
                                        <input
                                            type="text"
                                            value={newCustomer.postal_code}
                                            onChange={(e) => setNewCustomer({ ...newCustomer, postal_code: e.target.value })}
                                            className="w-full border rounded px-3 py-2"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Property Type</label>
                                    <select
                                        value={newCustomer.property_type}
                                        onChange={(e) => setNewCustomer({ ...newCustomer, property_type: e.target.value })}
                                        className="w-full border rounded px-3 py-2"
                                    >
                                        <option value="House">House</option>
                                        <option value="House – Basement">House – Basement</option>
                                        <option value="House – Front Door">House – Front Door</option>
                                        <option value="Apartment">Apartment</option>
                                        <option value="Condo">Condo</option>
                                        <option value="Townhouse">Townhouse</option>
                                        <option value="Business">Business</option>
                                        <option value="Office">Office</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Delivery Instructions / Buzz Code</label>
                                    <input
                                        type="text"
                                        value={newCustomer.delivery_instruction}
                                        onChange={(e) => setNewCustomer({ ...newCustomer, delivery_instruction: e.target.value })}
                                        className="w-full border rounded px-3 py-2"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Plan Selection */}
                        <div className="mt-8 space-y-4">
                            <h4 className="font-semibold text-gray-700 border-b pb-2">Plan Selection</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Meal Plan Type */}
                                <div>
                                    <label className="block text-sm font-medium mb-1">Meal Plan Type *</label>
                                    <select
                                        value={newCustomer.meal_plan_type}
                                        onChange={(e) => {
                                            const newType = e.target.value;
                                            const terms = Object.keys(livePricing?.plans?.[newType] || {});
                                            const firstBase = terms.length > 0 ? terms[0].split('|')[0] : '5 Days Veg';
                                            setNewCustomer({ ...newCustomer, meal_plan_type: newType, meal_plan_term: firstBase, nv_preference: '', nv_days: [] });
                                        }}
                                        className="w-full border rounded px-3 py-2 bg-white"
                                    >
                                        {livePricing?.plans
                                            ? Object.keys(livePricing.plans).map(t => <option key={t} value={t}>{t}</option>)
                                            : <option value="2 Meals a Day">2 Meals a Day</option>
                                        }
                                    </select>
                                </div>

                                {/* Meal Plan Term */}
                                <div>
                                    <label className="block text-sm font-medium mb-1">Meal Plan Term *</label>
                                    <select
                                        value={newCustomer.meal_plan_term}
                                        onChange={(e) => setNewCustomer({ ...newCustomer, meal_plan_term: e.target.value, nv_preference: '', nv_days: [] })}
                                        className="w-full border rounded px-3 py-2 bg-white"
                                    >
                                        {getMealPlanTerms().map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>

                                {/* NV Preference — only shown when NV sub-options exist */}
                                {getNvPreferences().length > 0 && (
                                    <div>
                                        <label className="block text-sm font-medium mb-1">NV Preference *</label>
                                        <select
                                            value={newCustomer.nv_preference}
                                            onChange={(e) => setNewCustomer({ ...newCustomer, nv_preference: e.target.value })}
                                            className="w-full border rounded px-3 py-2 bg-white"
                                        >
                                            <option value="">— Select —</option>
                                            {getNvPreferences().map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    </div>
                                )}
                            </div>

                            {/* NV Days picker — shown only for "2 Days Non-Veg, 3 Days Veg" */}
                            {newCustomer.meal_plan_term === '2 Days Non-Veg, 3 Days Veg' && (
                                <div className="mt-2">
                                    <label className="block text-sm font-medium mb-1">Non-Veg Days (pick exactly 2) *</label>
                                    <div className="flex flex-wrap gap-2">
                                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => {
                                            const isSelected = (newCustomer.nv_days || []).includes(day);
                                            const isDisabled = !isSelected && (newCustomer.nv_days || []).length >= 2;
                                            return (
                                                <button
                                                    key={day}
                                                    type="button"
                                                    disabled={isDisabled}
                                                    onClick={() => {
                                                        const updated = isSelected
                                                            ? newCustomer.nv_days.filter(d => d !== day)
                                                            : [...(newCustomer.nv_days || []), day];
                                                        setNewCustomer({ ...newCustomer, nv_days: updated });
                                                    }}
                                                    className={`px-3 py-1.5 rounded border text-sm font-medium transition-colors ${
                                                        isSelected
                                                            ? 'bg-orange-500 text-white border-orange-500'
                                                            : isDisabled
                                                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                                : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
                                                    }`}
                                                >
                                                    {day}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Add-ons Selection */}
                            {livePricing?.addons && Object.keys(livePricing.addons).length > 0 && (
                                <div className="mt-2">
                                    <label className="block text-sm font-medium mb-2">Add-ons (Select Multiple)</label>
                                    <div className="grid grid-cols-2 gap-2 border border-gray-300 rounded p-3 bg-gray-50 max-h-32 overflow-y-auto">
                                        {Object.keys(livePricing.addons).map(addon => (
                                            <label key={addon} className="flex items-center space-x-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={newCustomer.add_ons.includes(addon)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setNewCustomer({ ...newCustomer, add_ons: [...newCustomer.add_ons, addon] });
                                                        } else {
                                                            setNewCustomer({ ...newCustomer, add_ons: newCustomer.add_ons.filter(a => a !== addon) });
                                                        }
                                                    }}
                                                    className="rounded border-gray-300"
                                                />
                                                <span className="text-sm">
                                                    {addon}
                                                    <span className="text-xs text-gray-500 ml-1">
                                                        ({formatCurrency(livePricing.addons[addon]?.[newCustomer.plan_term] || 0)})
                                                    </span>
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                    {newCustomer.add_ons.length > 0 && (
                                        <p className="text-xs text-gray-600 mt-1">
                                            Selected: {newCustomer.add_ons.join(', ')}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Billing + Price Row */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Billing Cycle</label>
                                    <select
                                        value={newCustomer.plan_term}
                                        onChange={(e) => setNewCustomer({ ...newCustomer, plan_term: e.target.value })}
                                        className="w-full border rounded px-3 py-2 bg-white"
                                    >
                                        <option value="Weekly">Weekly</option>
                                        <option value="Monthly">Monthly</option>
                                    </select>
                                </div>
                                <div className="flex items-end col-span-2">
                                    <div className="flex gap-3 w-full">
                                        <div className="bg-green-50 border border-green-200 rounded px-4 py-2 flex-1 text-center">
                                            <span className="text-xs text-green-700 font-medium">Plan Price</span>
                                            <p className="text-lg font-bold text-green-800">
                                                {formatCurrency(getCalculatedPlanPrice())}
                                            </p>
                                        </div>
                                        {newCustomer.add_ons.length > 0 && (
                                            <div className="bg-blue-50 border border-blue-200 rounded px-4 py-2 flex-1 text-center">
                                                <span className="text-xs text-blue-700 font-medium">Add-ons</span>
                                                <p className="text-lg font-bold text-blue-800">
                                                    {formatCurrency(sumAddOnsPrice(newCustomer.add_ons, newCustomer.plan_term))}
                                                </p>
                                            </div>
                                        )}
                                        <div className="bg-emerald-50 border border-emerald-200 rounded px-4 py-2 flex-1 text-center">
                                            <span className="text-xs text-emerald-700 font-medium">Total</span>
                                            <p className="text-lg font-bold text-emerald-800">
                                                {formatCurrency(getCalculatedTotalPrice())}
                                                <span className="text-xs font-normal text-emerald-600 ml-1">/ {newCustomer.plan_term.toLowerCase()}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Dates + Amount Row */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        value={newCustomer.plan_start_date}
                                        onChange={(e) => {
                                            const selectedDate = e.target.value;
                                            if (selectedDate) {
                                                const day = new Date(selectedDate).getUTCDay();
                                                if (day === 0 || day === 6) {
                                                    alert('Please select a weekday (Monday-Friday) for the Start Date. Delivery is not available on Saturday and Sunday.');
                                                    return;
                                                }
                                            }
                                            setNewCustomer({ ...newCustomer, plan_start_date: selectedDate });
                                        }}
                                        className={`w-full border rounded px-3 py-2 ${newCustomer.plan_start_date && (new Date(newCustomer.plan_start_date).getUTCDay() === 0 || new Date(newCustomer.plan_start_date).getUTCDay() === 6)
                                            ? 'border-red-500 bg-red-50' : ''
                                            }`}
                                    />
                                    {(new Date(newCustomer.plan_start_date).getUTCDay() === 0 || new Date(newCustomer.plan_start_date).getUTCDay() === 6) && (
                                        <p className="text-[10px] text-red-600 mt-1">Weekend selected. Please choose a weekday.</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        End Date <span className="text-blue-600">(Auto)</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={newCustomer.plan_end_date}
                                        className="w-full border rounded px-3 py-2 bg-blue-50 border-blue-200 cursor-not-allowed"
                                        readOnly
                                        disabled
                                    />
                                    {newCustomer.plan_start_date && (
                                        <p className="text-[10px] text-blue-600 mt-1">Renewal: {newCustomer.plan_renewal_date}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Amount Paid (Optional)</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                                        <input
                                            type="number"
                                            value={newCustomer.amount_paid}
                                            onChange={(e) => setNewCustomer({ ...newCustomer, amount_paid: parseFloat(e.target.value) || 0 })}
                                            className="w-full border rounded pl-7 py-2"
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">If 0, plan price ({formatCurrency(getCalculatedPlanPrice())}) is used.</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-8">
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateCustomer}
                                disabled={creatingCustomer}
                                className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                            >
                                {creatingCustomer ? 'Creating...' : 'Create Customer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Edit Modal */}
            {showEditModal && editingCustomer && (
                <CustomerEditModal
                    customer={editingCustomer}
                    onSave={saveEdit}
                    onClose={() => setShowEditModal(false)}
                />
            )}

            {/* Pause Modal */}
            {showPauseModal && pausingCustomer && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
                        <h3 className="text-xl font-bold mb-4">
                            Pause Subscription
                        </h3>
                        <p className="mb-4 text-gray-600">
                            Pausing <strong>{pausingCustomer.first_name} {pausingCustomer.last_name}</strong>
                        </p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Pause Type</label>
                                <div className="flex gap-4">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="pauseType"
                                            value="custom_date"
                                            checked={pauseType === 'custom_date'}
                                            onChange={(e) => setPauseType(e.target.value)}
                                            className="mr-2"
                                        />
                                        Specific Date
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="pauseType"
                                            value="custom_range"
                                            checked={pauseType === 'custom_range'}
                                            onChange={(e) => setPauseType(e.target.value)}
                                            className="mr-2"
                                        />
                                        Date Range
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="pauseType"
                                            value="indefinite"
                                            checked={pauseType === 'indefinite'}
                                            onChange={(e) => setPauseType(e.target.value)}
                                            className="mr-2"
                                        />
                                        Indefinite
                                    </label>
                                </div>
                            </div>

                            {pauseType === 'custom_date' && (
                                <div>
                                    <label className="block text-sm font-medium mb-1">Pause Date (1 day)</label>
                                    <input
                                        type="date"
                                        value={pauseStartDate}
                                        onChange={(e) => setPauseStartDate(e.target.value)}
                                        className="w-full border rounded px-3 py-2"
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Subscription will resume automatically the next day.</p>
                                </div>
                            )}

                            {pauseType === 'custom_range' && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Start Date</label>
                                        <input
                                            type="date"
                                            value={pauseStartDate}
                                            onChange={(e) => setPauseStartDate(e.target.value)}
                                            className="w-full border rounded px-3 py-2"
                                            min={new Date().toISOString().split('T')[0]}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">End Date (Inclusive)</label>
                                        <input
                                            type="date"
                                            value={pauseEndDate}
                                            onChange={(e) => setPauseEndDate(e.target.value)}
                                            className="w-full border rounded px-3 py-2"
                                            min={pauseStartDate}
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 col-span-2">Subscription will resume automatically after the end date.</p>
                                </div>
                            )}

                            {pauseType === 'indefinite' && (
                                <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                                    <p className="text-sm text-yellow-800">
                                        ⚠️ Subscription will be paused until manually unpaused.
                                        Renewal date will be pushed forward by the number of days paused upon resumption.
                                    </p>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium mb-1">Reason (Optional)</label>
                                <textarea
                                    value={pauseReason}
                                    onChange={(e) => setPauseReason(e.target.value)}
                                    className="w-full border rounded px-3 py-2"
                                    rows="2"
                                    placeholder="e.g. Going on vacation"
                                ></textarea>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setShowPauseModal(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={pauseCustomer}
                                className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                            >
                                Confirm Pause
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default CustomersTab;
